// Display text for copy the API sends (see README "Backend-provided labels" and
// "Backend messages (codes)"). React-free and recruiter-only: never import it
// from a candidate-boundary file.
//
// - backendLabelOr (K1): the API sends English plus a stable id. Translate only
//   when the English bundle has the key AND its English still matches what the
//   backend sent, so reworded or UI-swapped text shows unchanged.
// - backendMessage (K3): the API sends a code plus params and keeps its English
//   only as a fallback. The English bundle owns the wording.
//
// Pass the English bundle of the namespace `t` defaults to (the first one given
// to useTranslation / getFixedT).
import { formatConjunctionList } from 'utils/appLocale';
import { hasOwnKey } from 'utils/typeGuards';

/** An English recruiter bundle (`locales/en/recruiter/<ns>.json`). */
export type EnglishBundle = Readonly<Record<string, string>>;

type StripPlural<K extends string> = K extends `${infer Base}_zero`
  ? Base
  : K extends `${infer Base}_one`
    ? Base
    : K extends `${infer Base}_two`
      ? Base
      : K extends `${infer Base}_few`
        ? Base
        : K extends `${infer Base}_many`
          ? Base
          : K extends `${infer Base}_other`
            ? Base
            : K;

/** A key of `Bundle` as `t` takes it: plural keys by their base name. */
export type BundleKey<Bundle extends EnglishBundle> = StripPlural<
  Extract<keyof Bundle, string>
>;

/** A `t` bound to the bundle's namespace (a `TFunction` fits). */
export type BackendLabelT<Bundle extends EnglishBundle> = (
  key: BundleKey<Bundle>
) => string;

export type BackendMessageValues = Record<string, string | number>;

/** A `t` bound to the bundle's namespace that takes interpolation values. */
export type BackendMessageT<Bundle extends EnglishBundle> = (
  key: BundleKey<Bundle>,
  values: BackendMessageValues
) => string;

/** One param of a backend message (a field of its `<Area>MessageParams`). */
export type BackendMessageParamValue =
  | string
  | number
  | readonly string[]
  | null
  | undefined;

/** Any generated params model: every field is a string, number or list. */
export type BackendMessageParams<Params> = {
  [Name in keyof Params]: BackendMessageParamValue;
};

/** A K3 message as the API sends it. */
export interface BackendMessage<Params extends BackendMessageParams<Params>> {
  code: string;
  params?: Params | null;
}

const ENGLISH_PLURAL_SUFFIXES = ['_one', '_other'] as const;

const collapseWhitespace = (text: string): string =>
  text.replace(/\s+/g, ' ').trim();

/**
 * Key segment for a code: `MISSING_JOB_SCRIPT` becomes `missingJobScript`.
 * Must match `code_key_segment` in server/i18n/recruiter_label_types.py.
 */
export const codeKeySegment = (code: string): string => {
  const [first = '', ...rest] = code.toLowerCase().split('_');
  return (
    first +
    rest.map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join('')
  );
};

const isPluralOnly = (bundle: EnglishBundle, key: string): boolean =>
  !hasOwnKey(bundle, key) &&
  ENGLISH_PLURAL_SUFFIXES.every((suffix) => hasOwnKey(bundle, key + suffix));

const isBundleKey = <Bundle extends EnglishBundle>(
  bundle: Bundle,
  key: string
): key is BundleKey<Bundle> & string =>
  hasOwnKey(bundle, key) || isPluralOnly(bundle, key);

/**
 * `t(key)` when the English bundle has `key` with the same English as
 * `english` (whitespace-insensitive, like the backend parity test); otherwise
 * `english` unchanged.
 */
export function backendLabelOr<Bundle extends EnglishBundle>(
  t: BackendLabelT<Bundle>,
  enBundle: Bundle,
  key: string,
  english: string
): string {
  if (!english || !hasOwnKey(enBundle, key) || !isBundleKey(enBundle, key)) {
    return english;
  }
  const bundleEnglish = enBundle[key];
  if (
    bundleEnglish === undefined ||
    collapseWhitespace(bundleEnglish) !== collapseWhitespace(english)
  ) {
    return english;
  }
  return t(key);
}

/**
 * i18next interpolation values for backend params: list params joined in the
 * display language ("A, B and C"), null params left out.
 */
export const interpolationValues = <
  Params extends BackendMessageParams<Params>,
>(
  params: Params | null | undefined
): BackendMessageValues => {
  const values: BackendMessageValues = {};
  if (!params) {
    return values;
  }
  for (const name of Object.keys(params)) {
    if (!hasOwnKey(params, name)) {
      continue;
    }
    const value: BackendMessageParamValue = params[name];
    if (value == null) {
      continue;
    }
    values[name] =
      typeof value === 'string' || typeof value === 'number'
        ? value
        : formatConjunctionList(value);
  }
  return values;
};

/**
 * The UI's text for a K3 message: `t('<prefix>.<camelCode>', params)` when the
 * English bundle has that key (or its `_one` / `_other` forms, with a numeric
 * `count` param), else `englishFallback` (the message's InternalText English,
 * for legacy rows and version skew). List params are joined in the display
 * language ("A, B and C"); null params are left out.
 */
export function backendMessage<
  Bundle extends EnglishBundle,
  Params extends BackendMessageParams<Params>,
>(
  t: BackendMessageT<Bundle>,
  enBundle: Bundle,
  prefix: string,
  message: BackendMessage<Params>,
  englishFallback: string
): string {
  const key = `${prefix}.${codeKeySegment(message.code)}`;
  if (!isBundleKey(enBundle, key)) {
    return englishFallback;
  }
  const values = interpolationValues(message.params);
  if (isPluralOnly(enBundle, key) && typeof values.count !== 'number') {
    return englishFallback;
  }
  return t(key, values);
}
